import React, { useState, useEffect } from 'react';
import RegistrationProgressBar from '../../components/usercomponents/RegistrationProgressBar';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaInfoCircle } from 'react-icons/fa';
import './registrationcss/Register.css';
import personalInfoImg from '../../images/PersonalInfo_IMG.png';
import { generateUIC, storeUIC } from '../../services/uicService';
import logo from '../../images/auravit_logo.png';

const currentStep = 1;

// --- VALIDATION CONSTANTS ---
const NAME_REGEX = /^[A-Za-z\s'-]*$/;
const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = CURRENT_YEAR - 75; // 75 years ago
const MAX_YEAR = CURRENT_YEAR - 10; // Current year - 10
const MIN_AGE = 10;
const MINOR_AGE_LIMIT = 18;
const SENIOR_AGE_LIMIT = 60;

// Helper to calculate age from YYYY-MM-DD
const calculateAge = (dob) => {
  if (!dob) return 0;
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Calculate DOB constraints
const minDobDate = new Date(MIN_YEAR, 0, 1); // Jan 1, 75 years ago
const MIN_DOB = `${MIN_YEAR}-01-01`;

const todayDate = new Date();
const TODAY_STR = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;

const maxDobDate = new Date(MAX_YEAR, 11, 31); // Dec 31, current year - 10
const MAX_DOB_ALLOWED = `${MAX_YEAR}-12-31`;

// Helper function to get days in month (accounts for leap years)
const getDaysInMonth = (month, year) => {
  const monthNum = parseInt(month);
  const yearNum = parseInt(year);
  if (isNaN(monthNum) || isNaN(yearNum)) return 31;
  return new Date(yearNum, monthNum, 0).getDate();
};

// Generate month options (01-12)
const generateMonthOptions = () => {
  return Array.from({ length: 12 }, (_, i) => {
    const monthNum = i + 1;
    return {
      value: String(monthNum).padStart(2, '0'),
      label: String(monthNum).padStart(2, '0')
    };
  });
};

// Generate day options based on month and year
const generateDayOptions = (month, year) => {
  const daysInMonth = getDaysInMonth(month, year);
  return Array.from({ length: daysInMonth }, (_, i) => {
    const dayNum = i + 1;
    return {
      value: String(dayNum).padStart(2, '0'),
      label: String(dayNum).padStart(2, '0')
    };
  });
};

// Generate year options (75 years ago to current year - 10)
const generateYearOptions = () => {
  const years = [];
  for (let year = MIN_YEAR; year <= MAX_YEAR; year++) {
    years.push({
      value: String(year),
      label: String(year)
    });
  }
  return years.reverse(); // Show most recent years first
};

// --- VALIDATION HELPER FUNCTIONS ---

/**
 * Validates a name field (first, middle, last)
 * @param {string} name - The name to validate
 * @param {string} fieldType - 'first', 'middle', or 'last'
 * @returns {string} - Empty string if valid, error message otherwise
 */
const validateName = (name, fieldType = 'name') => {
  if (!name || !name.trim()) {
    return `${fieldType.charAt(0).toUpperCase() + fieldType.slice(1)} name is required`;
  }
  if (!NAME_REGEX.test(name)) {
    return `${fieldType.charAt(0).toUpperCase() + fieldType.slice(1)} name can only contain letters, spaces, hyphens, and apostrophes`;
  }
  if (name.length > 50) {
    return `${fieldType.charAt(0).toUpperCase() + fieldType.slice(1)} name must not exceed 50 characters`;
  }
  return '';
};

/**
 * Validates date of birth
 * @param {string} dob - ISO format date string (YYYY-MM-DD)
 * @returns {string} - Empty string if valid, error message otherwise
 */
const validateDob = (dob) => {
  if (!dob) {
    return 'Date of birth is required';
  }
  
  const selectedDate = new Date(dob);
  const today = new Date();
  
  if (isNaN(selectedDate.getTime())) {
    return 'Invalid date format';
  }
  
  if (selectedDate < minDobDate) {
    return `You must be born after ${MIN_YEAR}`;
  }
  
  if (selectedDate > maxDobDate) {
    return `You must be at least ${MIN_AGE} years old to register`;
  }
  
  return '';
};

/**
 * Validates sex selection
 * @param {string} sex - Sex value
 * @returns {string} - Empty string if valid, error message otherwise
 */
const validateSex = (sex) => {
  if (!sex || !sex.trim()) {
    return 'Sex assigned at birth is required';
  }
  if (!['male', 'female'].includes(sex.toLowerCase())) {
    return 'Invalid sex selection';
  }
  return '';
};

/**
 * Clamps DOB to valid range if outside bounds
 * @param {string} dob - ISO format date string
 * @returns {string} - Clamped date in ISO format
 */
const clampDobToValidRange = (dob) => {
  if (!dob) return dob;
  
  const selectedDate = new Date(dob + 'T00:00:00'); // Force local time
  let clampedDate = selectedDate;
  
  if (selectedDate < minDobDate) {
    clampedDate = new Date(minDobDate);
  } else if (selectedDate > maxDobDate) {
    clampedDate = new Date(maxDobDate);
  }
  
  // Format as YYYY-MM-DD using local time
  const year = clampedDate.getFullYear();
  const month = String(clampedDate.getMonth() + 1).padStart(2, '0');
  const day = String(clampedDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Firebase-only registration: step 1 is passed forward and saved when the account is created.

function PersonalInformation() {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    sex: '',
    motherFirstName: '',
    fatherFirstName: '',
    birthOrder: '',
  });

  const [dobMonth, setDobMonth] = useState('');
  const [dobDay, setDobDay] = useState('');
  const [dobYear, setDobYear] = useState('');

  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showUicModal, setShowUicModal] = useState(false);

  const storedDraft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
  const assistanceRequired = storedDraft.assistanceRequired === 'yes';
  const reasonForAssistance = storedDraft.reasonForAssistance || [];
  const isMinorFlow = Array.isArray(reasonForAssistance) 
    ? reasonForAssistance.includes('minor')
    : reasonForAssistance === 'minor';

    const isSeniorFlow = Array.isArray(reasonForAssistance) 
    ? reasonForAssistance.includes('senior')
    : reasonForAssistance === 'senior';

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validate and restrict name input in real-time
    if (['firstName', 'lastName', 'motherFirstName', 'fatherFirstName'].includes(name)) {
      if (!NAME_REGEX.test(value)) return; // Block invalid characters
      if (value.length > 50) return; // Limit length
    }

    // Validate birth order (numbers only, max 2 digits)
    if (name === 'birthOrder') {
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length > 2) return; // Limit to 2 digits
      
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
      
      // Real-time validation for birth order
      if (hasSubmitted && numericValue) {
        setErrors((prev) => {
          const updated = { ...prev };
          
          // Clear existing birth order error
          if (updated.birthOrder) {
            delete updated.birthOrder;
          }
          
          // Check for 00 validation
          if (numericValue === '00') {
            updated.birthOrder = 'Invalid Birth Order';
          } else if (!/^\d{2}$/.test(numericValue.trim())) {
            updated.birthOrder = 'Birth order must be exactly 2 digits (01, 02, 03...)';
          } else if (parseInt(numericValue) < 1 || parseInt(numericValue) > 99) {
            updated.birthOrder = 'Birth order must be between 01 and 99';
          }
          
          return updated;
        });
      }
      
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear errors for this field if form has been submitted
    if (hasSubmitted) {
      setErrors((prev) => {
        const updated = { ...prev };
        
        // Clear error for this field
        if (updated[name]) {
          delete updated[name];
        }
        
        return updated;
      });
    }
  };

  const handleDobChange = (field, value) => {
    if (field === 'month') {
      setDobMonth(value);
      // Reset day if it's no longer valid for the new month
      const maxDays = getDaysInMonth(value, dobYear);
      if (dobDay && parseInt(dobDay) > maxDays) {
        setDobDay('');
      }
    } else if (field === 'day') {
      setDobDay(value);
    } else if (field === 'year') {
      setDobYear(value);
      // Reset day if it's no longer valid for the new year (leap year)
      const maxDays = getDaysInMonth(dobMonth, value);
      if (dobDay && parseInt(dobDay) > maxDays) {
        setDobDay('');
      }
    }

    // Update formData.dob when all three are selected
    // Use the current values (including the one just set)
    const currentMonth = field === 'month' ? value : dobMonth;
    const currentDay = field === 'day' ? value : dobDay;
    const currentYear = field === 'year' ? value : dobYear;
    
    if (currentMonth && currentDay && currentYear) {
      const dobString = `${currentYear}-${currentMonth}-${currentDay}`;
      setFormData((prev) => ({ ...prev, dob: dobString }));
    } else {
      setFormData((prev) => ({ ...prev, dob: '' }));
      // Clear error when incomplete (unless form was submitted)
      if (!hasSubmitted) {
        setErrors((prev) => {
          const updated = { ...prev };
          delete updated.dob;
          return updated;
        });
      } else {
        setErrors((prev) => ({ ...prev, dob: 'Date of birth is required' }));
      }
    }
  };

  const validateDobWithAssistance = (dob) => {
    const age = calculateAge(dob);
    const isMinor = age < MINOR_AGE_LIMIT;
    const isSenior = age >= SENIOR_AGE_LIMIT;
    const dobError = validateDob(dob);
    let finalError = dobError;

    // When assistance is set to No
    if (!assistanceRequired) {
      if (isSenior) {
        finalError = "You have entered a senior DOB. If you are a senior, change Assistance to \"Yes - Senior\".";
      } else if (isMinor) {
        finalError = "You have entered a minor DOB. If you are a minor, change Assistance to \"Yes - Minor\".";
      }
    }
    // When assistance is set to Yes - Senior
    else if (isSeniorFlow) {
      if (!dobError && !isSenior) {
        finalError = "Date of birth must belong to a senior.";
      }
    }
    // When assistance is set to Yes - Minor
    else if (isMinorFlow) {
      if (!dobError && !isMinor) {
        finalError = "Date of birth belong to a minor.";
      }
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      if (finalError) {
        newErrors.dob = finalError;
      } else {
        delete newErrors.dob;
      }
      return newErrors;
    });
  };

  const validate = () => {
    const newErrors = {};
    
    // Validate first name
    const firstNameError = validateName(formData.firstName, 'first');
    if (firstNameError) newErrors.firstName = firstNameError;
    
    // Validate last name
    const lastNameError = validateName(formData.lastName, 'last');
    if (lastNameError) newErrors.lastName = lastNameError;
    
    // Validate date of birth with assistance logic
    const age = calculateAge(formData.dob);
    const isMinor = age < MINOR_AGE_LIMIT;
    const isSenior = age >= SENIOR_AGE_LIMIT;
    const dobError = validateDob(formData.dob);
    
    if (dobError) {
      newErrors.dob = dobError;
    } else {
      // When assistance is set to No
      if (!assistanceRequired) {
        if (isSenior) {
          newErrors.dob = "You have entered a senior DOB. If you are a senior, change Assistance to \"Yes - Senior\".";
        } else if (isMinor) {
          newErrors.dob = "You have entered a minor DOB. If you are a minor, change Assistance to \"Yes - Minor\".";
        }
      }
      // When assistance is set to Yes - Senior
      else if (isSeniorFlow) {
        if (!isSenior) {
          newErrors.dob = "Date of birth must belong to a senior.";
        }
      }
      // When assistance is set to Yes - Minor
      else if (isMinorFlow) {
        if (!isMinor) {
          newErrors.dob = "Date of birth must belong to a minor.";
        }
      }
    }
    
    // Validate sex
    const sexError = validateSex(formData.sex);
    if (sexError) newErrors.sex = sexError;

    // Validate birth order (required)
    if (!formData.birthOrder || !formData.birthOrder.trim()) {
      newErrors.birthOrder = 'Birth order is required';
    } else if (!/^\d{2}$/.test(formData.birthOrder.trim())) {
      newErrors.birthOrder = 'Birth order must be exactly 2 digits (01, 02, 03...)';
    } else if (formData.birthOrder === '00') {
      newErrors.birthOrder = 'Birth order cannot be 00';
    } else if (parseInt(formData.birthOrder) < 1 || parseInt(formData.birthOrder) > 99) {
      newErrors.birthOrder = 'Birth order must be between 01 and 99';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    setHasSubmitted(true);

    if (!validate()) {
      return;
    }

    // Generate Unique Identifier Code (UIC)
    const uicParams = {
      motherFirstName: formData.motherFirstName,
      fatherFirstName: formData.fatherFirstName,
      userFirstName: formData.firstName,
      userLastName: formData.lastName,
      birthOrder: formData.birthOrder,
      dateOfBirth: formData.dob
    };

    const generatedUIC = generateUIC(uicParams);
    
    // Store UIC in localStorage
    storeUIC(generatedUIC);

    const storedDraft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
    const updatedDraft = {
      ...storedDraft,
      firstName: formData.firstName,
      lastName: formData.lastName,
      dob: formData.dob,
      sex: formData.sex,
      motherFirstName: formData.motherFirstName,
      fatherFirstName: formData.fatherFirstName,
      birthOrder: formData.birthOrder,
      uniqueIdentifierCode: generatedUIC,
      registrationStep: 1,
    };

    localStorage.setItem('registrationDraft', JSON.stringify(updatedDraft));

    navigate('/contact-details', {
      state: { ...updatedDraft, isMinor: isMinorFlow }
    });
  };


  // Add fade-in animation on mount
  useEffect(() => {
    const registerCard = document.querySelector('.register-card');
    if (registerCard) {
      registerCard.classList.add('fade-in');
    }
  }, []);

  // Hydrate from saved draft when navigating back
  useEffect(() => {
    const draft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
    if (!draft.firstName && !draft.lastName && !draft.dob && !draft.sex) return;

    setFormData({
      firstName: draft.firstName || '',
      lastName: draft.lastName || '',
      dob: draft.dob || '',
      sex: draft.sex || '',
      motherFirstName: draft.motherFirstName || '',
      fatherFirstName: draft.fatherFirstName || '',
      birthOrder: draft.birthOrder || '',
    });

    if (draft.dob) {
      const [year, month, day] = draft.dob.split('-');
      setDobYear(year || '');
      setDobMonth(month || '');
      setDobDay(day || '');
    }
  }, []);

  // Re-validate DOB when assistance selection changes
  useEffect(() => {
    if (formData.dob) {
      validateDobWithAssistance(formData.dob);
    }
  }, [assistanceRequired, isMinorFlow, isSeniorFlow]);

  // Validate DOB when all three dropdowns are filled
  useEffect(() => {
    if (dobMonth && dobDay && dobYear) {
      const dobString = `${dobYear}-${dobMonth}-${dobDay}`;
      setFormData((prev) => ({ ...prev, dob: dobString }));
      validateDobWithAssistance(dobString);
    }
  }, [dobMonth, dobDay, dobYear]);

  return (
    <>
      <div className="login-logo-container">
        <img 
          src={logo} 
          alt="AuraVit" 
          className="login-logo"
          onClick={() => navigate("/")}
        />
      </div>
      <div className="register-container">
      <div className="register-card">
        <div className="left-panel">
          <img
            src={personalInfoImg}
            alt="Personal information"
            className="panel-image"
          />
        </div>
        <div className="right-panel">
          <div className="registration-header-left">
            <h1 className="create-account-title">Create Account</h1>
            <p className="wellness-subtitle">
              A safe space for your health. Let’s navigate your wellness together.
            </p>
          </div>

          <RegistrationProgressBar currentStep={2} />

          {/* Display submit error if backend save fails */}
          {errors.submit && (
            <div className="alert alert-error" role="alert">
              {errors.submit}
            </div>
          )}

          <form noValidate>
            {/* First Name and Last Name */}
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="firstName">
                  First Name <span className="required">*</span>
                </label>
                <input
                  id="firstName"
                  type="text"
                  name="firstName"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={errors.firstName ? 'error' : ''}
                  maxLength="50"
                  disabled={isSaving}
                  required
                />
                {errors.firstName && (
                  <span className="field-error">{errors.firstName}</span>
                )}
              </div>

              <div className="input-group">
                <label htmlFor="lastName">
                  Last Name <span className="required">*</span>
                </label>
                <input
                  id="lastName"
                  type="text"
                  name="lastName"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={errors.lastName ? 'error' : ''}
                  maxLength="50"
                  disabled={isSaving}
                  required
                />
                {errors.lastName && (
                  <span className="field-error">{errors.lastName}</span>
                )}
              </div>
            </div>

            {/* Sex and Date of Birth */}
            <div className="form-row">
              <div className="input-group select-wrapper">
                <label htmlFor="sex">
                  Sex (at birth) <span className="required">*</span>
                </label>
                <select
                  id="sex"
                  name="sex"
                  value={formData.sex}
                  onChange={handleChange}
                  className={errors.sex ? 'error' : ''}
                  disabled={isSaving}
                  required
                >
                  <option value="" disabled hidden>
                    Select Sex
                  </option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
                <span className="dropdown-icon" />
                {errors.sex && <span className="field-error">{errors.sex}</span>}
              </div>

              <div className="input-group">
                <label>
                  Date of Birth <span className="required">*</span>
                </label>
                <div className="dob-dropdowns">
                  <select
                    value={dobMonth}
                    onChange={(e) => {
                      handleDobChange('month', e.target.value);
                    }}
                    className={errors.dob ? 'error' : ''}
                    disabled={isSaving}
                  >
                    <option value="" disabled>MM</option>
                    {generateMonthOptions().map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <select
                    value={dobDay}
                    onChange={(e) => {
                      handleDobChange('day', e.target.value);
                    }}
                    className={errors.dob ? 'error' : ''}
                    disabled={isSaving}
                  >
                    <option value="" disabled>DD</option>
                    {generateDayOptions(dobMonth, dobYear).map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <select
                    value={dobYear}
                    onChange={(e) => {
                      handleDobChange('year', e.target.value);
                    }}
                    className={errors.dob ? 'error' : ''}
                    disabled={isSaving}
                  >
                    <option value="" disabled>YYYY</option>
                    {generateYearOptions().map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                {errors.dob && <span className="field-error">{errors.dob}</span>}
              </div>
            </div>

            {/* Unique Identifier Code Information */}
            <div className="form-row">
              <div className="input-group">
                <div
                  className="uic-info-text"
                  style={{
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowUicModal(true)}
                >
                  Unique Identifier Code Information
                  <FaInfoCircle
                    style={{
                      color: '#666',
                      fontSize: '16px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#007bff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
                  />
                </div>
              </div>
            </div>

            {/* Parent Information */}
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="motherFirstName">
                  Mother's First Name
                </label>
                <input
                  id="motherFirstName"
                  type="text"
                  name="motherFirstName"
                  placeholder="Enter mother's first name"
                  value={formData.motherFirstName}
                  onChange={handleChange}
                  maxLength="20"
                  disabled={isSaving}
                />
                <small className="helper-text" style={{ textAlign: 'left', color: '#999999' }}>If information unavailable, leave blank</small>
                {errors.motherFirstName && <span className="field-error">{errors.motherFirstName}</span>}
              </div>

              <div className="input-group">
                <label htmlFor="fatherFirstName">
                  Father's First Name
                </label>
                <input
                  id="fatherFirstName"
                  type="text"
                  name="fatherFirstName"
                  placeholder="Enter father's first name"
                  value={formData.fatherFirstName}
                  onChange={handleChange}
                  maxLength="20"
                  disabled={isSaving}
                />
                <small className="helper-text" style={{ textAlign: 'left', color: '#999999' }}>If information unavailable, leave blank</small>
                {errors.fatherFirstName && <span className="field-error">{errors.fatherFirstName}</span>}
              </div>
            </div>

            {/* Birth Order */}
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="birthOrder">
                  Birth Order <span className="required">*</span>
                </label>
                <input
                  id="birthOrder"
                  type="text"
                  name="birthOrder"
                  placeholder=""
                  value={formData.birthOrder}
                  onChange={handleChange}
                  className={errors.birthOrder ? 'error' : ''}
                  maxLength="2"
                  minLength="2"
                  disabled={isSaving}
                  required
                />
                                {errors.birthOrder && <span className="field-error">{errors.birthOrder}</span>}
              </div>
            </div>

            {/* Continue Button */}
            <button
              type="button"
              className="continue-btn"
              onClick={handleContinue}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : isSuccess ? 'Saved! ✓' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>

    {/* UIC Information Modal */}
    {showUicModal && (
      <div
        className="modal-overlay"
        onClick={() => setShowUicModal(false)}
      >
        <div
          className="modal-content uic-modal-wide"
          style={{ maxWidth: '1100px', width: '90%', padding: '2.5rem 3.5rem', textAlign: 'left' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <h3>Why Is a Unique Identifier Code (UIC) Needed?</h3>
            <button
              onClick={() => setShowUicModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>
          <div style={{ lineHeight: '1.6', color: '#4a5568', textAlign: 'left', maxWidth: '100%' }}>
            <p style={{ marginBottom: '12px' }}>
              A <strong>Unique Identifier Code (UIC)</strong> is used during HIV testing to help protect your privacy and confidentiality. Instead of using your full name as the primary identifier, the UIC provides a secure way to associate your HIV testing information with your records.
            </p>
            <p style={{ marginBottom: '12px' }}>
              Your UIC is created using the following format:
            </p>
            <p style={{ marginBottom: '12px', fontStyle: 'italic', paddingLeft: '16px' }}>
              <strong>Mother's first name (first 2 letters) + Father's first name (first 2 letters) + Birth order + Date of birth</strong>
            </p>
            <p style={{ marginBottom: '12px' }}>
              <strong>Example:</strong>
            </p>
            <p style={{ marginBottom: '12px', paddingLeft: '16px' }}>
              Mother: Maria<br />
              Father: Juan<br />
              Birth order: 1st child<br />
              Date of birth: January 15, 2000
            </p>
            <p style={{ marginBottom: '12px', paddingLeft: '16px', fontWeight: '600', color: '#2c5282' }}>
              UIC: MAJU101152000
            </p>
            <p style={{ marginBottom: '12px' }}>
              If your mother's or father's first name is <strong>not available</strong>, the missing name component is replaced with your initials:
            </p>
            <p style={{ marginBottom: '12px', fontStyle: 'italic', paddingLeft: '16px' }}>
              <strong>First letter of your first name + First letter of your last name</strong>
            </p>
            <p style={{ marginBottom: '0' }}>
              The UIC helps healthcare providers <strong>identify and manage your testing records while reducing the need to display your full name</strong>. Your HIV testing information should still be handled with appropriate confidentiality and privacy safeguards.
            </p>
          </div>
        </div>
      </div>
    )}

    </>
  );
}

export default PersonalInformation;
